import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';
import { toLocalDateStr } from '../../../utils/dateUtils';

/**
 * WeekOverview - Widget "Ma semaine"
 *
 * Affiche un calendrier compact de la semaine courante avec :
 * - Les prestations de l'utilisateur (P = prestation, B = blocage)
 * - Les absences (CP, RTT, etc.)
 * - Navigation vers "Mon Planning"
 */
const WeekOverview = memo(({ token, userId, onNavigate }) => {
  const [data, setData] = useState({ prestations: [], absences: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calculer les dates de la semaine courante
  const weekDays = useMemo(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(12, 0, 0, 0);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      const dateStr = toLocalDateStr(date);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isToday = dateStr === toLocalDateStr(new Date());

      days.push({
        date: dateStr,
        dayName: date.toLocaleDateString('fr-FR', { weekday: 'short' }).toUpperCase(),
        dayNumber: date.getDate(),
        isWeekend,
        isToday
      });
    }
    return days;
  }, []);

  const startDate = weekDays[0]?.date;
  const endDate = weekDays[6]?.date;

  // Charger les données
  const fetchData = useCallback(async () => {
    if (!token || !userId) return;

    setLoading(true);
    try {
      // Charger les prestations de l'utilisateur
      const prestationsUrl = `${API_BASE_URL}/api/prestations/my-planning?start_date=${startDate}&end_date=${endDate}`;
      const prestationsRes = await fetch(prestationsUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Charger les absences de l'utilisateur
      const absencesUrl = `${API_BASE_URL}/api/users/${userId}/availability/${startDate}/${endDate}`;
      const absencesRes = await fetch(absencesUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      let prestations = [];
      let absences = {};

      if (prestationsRes.ok) {
        const prestationsData = await prestationsRes.json();
        prestations = prestationsData.data || prestationsData.prestations || [];
      }

      if (absencesRes.ok) {
        const absencesData = await absencesRes.json();
        // Transformer en map date -> type
        if (absencesData.data) {
          absencesData.data.forEach(a => {
            if (a.date && a.availability_type_code) {
              absences[a.date] = {
                code: a.availability_type_code,
                name: a.availability_type_name,
                color: a.availability_type_color || '#17a2b8'
              };
            }
          });
        }
      }

      setData({ prestations, absences });
      setError(null);
    } catch (err) {
      console.error('Erreur chargement semaine:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, userId, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Déterminer le contenu d'une journée
  const getDayContent = useCallback((dateStr) => {
    // Vérifier s'il y a une absence
    const absence = data.absences[dateStr];
    if (absence) {
      return {
        type: 'absence',
        label: absence.code,
        color: absence.color,
        title: absence.name
      };
    }

    // Vérifier s'il y a une prestation (jours d'affectation précis si disponibles)
    const prestation = data.prestations.find(p => {
      if (p.assigned_days && Array.isArray(p.assigned_days)) {
        return p.assigned_days.includes(dateStr);
      }
      const start = p.start_date?.split('T')[0];
      const end = p.end_date?.split('T')[0];
      return dateStr >= start && dateStr <= end;
    });

    if (prestation) {
      const isBlocked = prestation.type === 'blocked' || prestation.is_blocked;
      return {
        type: 'prestation',
        label: isBlocked ? 'B' : 'P',
        color: isBlocked ? '#ffc107' : '#28a745',
        title: prestation.client_name || prestation.prestation_number || 'Prestation',
        prestation
      };
    }

    return null;
  }, [data]);

  // Filtrer les week-ends sans activité
  const visibleDays = useMemo(() => {
    return weekDays.filter(day => {
      if (!day.isWeekend) return true;
      const content = getDayContent(day.date);
      return content !== null;
    });
  }, [weekDays, getDayContent]);

  const handleViewAll = () => {
    if (onNavigate) {
      onNavigate('planning');
    }
  };

  // IMPORTANT: Les hooks doivent être appelés AVANT les returns anticipés
  // pour respecter les règles des hooks React (même nombre de hooks à chaque render)
  const dynamicStyles = useMemo(() => getStyles(visibleDays.length || 5), [visibleDays.length]);

  if (loading) {
    return (
      <div style={dynamicStyles.container}>
        <div style={dynamicStyles.header}>
          <h3 style={dynamicStyles.title}>Ma semaine</h3>
        </div>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={dynamicStyles.container}>
        <div style={dynamicStyles.header}>
          <h3 style={dynamicStyles.title}>Ma semaine</h3>
        </div>
        <div style={styles.error}>Erreur de chargement</div>
      </div>
    );
  }

  return (
    <div style={dynamicStyles.container}>
      <div style={dynamicStyles.header}>
        <h3 style={dynamicStyles.title}>Ma semaine</h3>
        <button style={dynamicStyles.viewAllButton} onClick={handleViewAll}>
          Voir tout &rarr;
        </button>
      </div>
      <div style={dynamicStyles.weekGrid}>
        {visibleDays.map(day => {
          const content = getDayContent(day.date);
          return (
            <div
              key={day.date}
              style={{
                ...dynamicStyles.dayCell,
                ...(day.isToday ? dynamicStyles.dayCellToday : {}),
                ...(day.isWeekend ? dynamicStyles.dayCellWeekend : {})
              }}
              title={content?.title || 'Disponible'}
            >
              <span style={dynamicStyles.dayName}>{day.dayName}</span>
              <span style={dynamicStyles.dayNumber}>{day.dayNumber}</span>
              <div
                style={{
                  ...dynamicStyles.dayContent,
                  backgroundColor: content?.color || '#e9ecef',
                  color: content ? 'white' : '#adb5bd'
                }}
              >
                {content?.label || '·'}
              </div>
            </div>
          );
        })}
      </div>
      <div style={dynamicStyles.legend}>
        <span style={dynamicStyles.legendItem}>
          <span style={{ ...dynamicStyles.legendDot, backgroundColor: '#28a745' }}></span>
          P
        </span>
        <span style={dynamicStyles.legendItem}>
          <span style={{ ...dynamicStyles.legendDot, backgroundColor: '#ffc107' }}></span>
          B
        </span>
        <span style={dynamicStyles.legendItem}>
          <span style={{ ...dynamicStyles.legendDot, backgroundColor: '#17a2b8' }}></span>
          Abs
        </span>
      </div>
    </div>
  );
});

WeekOverview.displayName = 'WeekOverview';

// Fonction pour générer les styles (permet d'accéder à visibleDays.length)
const getStyles = (daysCount) => ({
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: 'clamp(12px, 3vw, 20px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    marginBottom: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  viewAllButton: {
    background: 'none',
    border: 'none',
    color: '#667eea',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    padding: '8px 12px',
    minHeight: '44px',
    borderRadius: '8px',
    transition: 'background-color 0.2s'
  },
  weekGrid: {
    display: 'grid',
    // Grille fixe basée sur le nombre de jours (évite le wrapping irrégulier)
    gridTemplateColumns: `repeat(${daysCount}, 1fr)`,
    gap: 'clamp(4px, 1.5vw, 8px)',
    marginBottom: '12px'
  },
  dayCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 'clamp(6px, 2vw, 10px) 4px',
    borderRadius: '8px',
    backgroundColor: '#f8f9fa',
    transition: 'transform 0.2s',
    minWidth: 0 // Permet le shrink sur mobile
  },
  dayCellToday: {
    border: '2px solid #667eea',
    backgroundColor: '#f0f1ff'
  },
  dayCellWeekend: {
    opacity: 0.7
  },
  dayName: {
    fontSize: 'clamp(0.6rem, 2vw, 0.75rem)',
    fontWeight: '600',
    color: '#6c757d',
    marginBottom: '2px'
  },
  dayNumber: {
    fontSize: 'clamp(0.85rem, 3vw, 1rem)',
    fontWeight: '600',
    color: '#1a1a2e',
    marginBottom: '4px'
  },
  dayContent: {
    width: 'clamp(26px, 8vw, 32px)',
    height: 'clamp(26px, 8vw, 32px)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
    fontWeight: '600'
  },
  legend: {
    display: 'flex',
    gap: 'clamp(8px, 2vw, 16px)',
    flexWrap: 'wrap',
    justifyContent: 'center',
    paddingTop: '12px',
    borderTop: '1px solid #e9ecef'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
    color: '#6c757d'
  },
  legendDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#6c757d'
  },
  error: {
    textAlign: 'center',
    padding: '40px',
    color: '#dc3545'
  }
});

// Conserver les styles statiques pour la compatibilité
const styles = getStyles(5);

export default WeekOverview;
